import { db } from "./firebase";
import { 
  collection, 
  doc, 
  getDocs, 
  setDoc, 
  deleteDoc, 
  getDoc,
  query,
  orderBy,
  limit,
  addDoc
} from "firebase/firestore";
import { BlogPost, SEED_POSTS } from "../components/BlogData";
import { CaseStudy, SEED_CASES } from "../components/CaseStudiesData";
import { ContactMessage } from "../types";

// ==========================================
// 1. BLOG POSTS
// ==========================================
export async function getBlogPostsFromFirebase(): Promise<BlogPost[]> {
  try {
    const colRef = collection(db, "blog_posts");
    const snapshot = await getDocs(colRef);
    if (snapshot.empty) {
      // Seed initial posts if Firestore is completely empty
      console.log("Seeding blog posts in Firestore...");
      for (const post of SEED_POSTS) {
        await setDoc(doc(db, "blog_posts", post.id), post);
      }
      return SEED_POSTS;
    }
    const posts: BlogPost[] = [];
    snapshot.forEach((doc) => {
      posts.push(doc.data() as BlogPost);
    });
    // Sort by date descending
    return posts.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (error) {
    console.warn("Error fetching blog posts from Firebase (using local fallback):", error);
    // Fallback to localStorage/SEED_POSTS if Firestore fails
    return SEED_POSTS;
  }
}

export async function saveBlogPostToFirebase(post: BlogPost): Promise<void> {
  try {
    await setDoc(doc(db, "blog_posts", post.id), post);
    console.log(`Successfully saved blog post ${post.id} to Firestore`);
  } catch (error) {
    console.warn(`Error saving blog post ${post.id} to Firestore (offline/queued):`, error);
  }
}

export async function deleteBlogPostFromFirebase(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, "blog_posts", id));
    console.log(`Successfully deleted blog post ${id} from Firestore`);
  } catch (error) {
    console.warn(`Error deleting blog post ${id} from Firestore:`, error);
  }
}

// ==========================================
// 2. CASE STUDIES
// ==========================================
export async function getCaseStudiesFromFirebase(): Promise<CaseStudy[]> {
  try {
    const colRef = collection(db, "case_studies");
    const snapshot = await getDocs(colRef);
    if (snapshot.empty) {
      console.log("Seeding case studies in Firestore...");
      for (const cs of SEED_CASES) {
        await setDoc(doc(db, "case_studies", cs.id), cs);
      }
      return SEED_CASES;
    }
    const cases: CaseStudy[] = [];
    snapshot.forEach((doc) => {
      cases.push(doc.data() as CaseStudy);
    });
    return cases.sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime());
  } catch (error) {
    console.warn("Error fetching case studies from Firebase (using local fallback):", error);
    return SEED_CASES;
  }
}

export async function saveCaseStudyToFirebase(cs: CaseStudy): Promise<void> {
  try {
    await setDoc(doc(db, "case_studies", cs.id), cs);
    console.log(`Successfully saved case study ${cs.id} to Firestore`);
  } catch (error) {
    console.warn(`Error saving case study ${cs.id} to Firestore:`, error);
  }
}

export async function deleteCaseStudyFromFirebase(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, "case_studies", id));
    console.log(`Successfully deleted case study ${id} from Firestore`);
  } catch (error) {
    console.warn(`Error deleting case study ${id} from Firestore:`, error);
  }
}

// ==========================================
// 3. CONTACT LEADS
// ==========================================
export async function getLeadsFromFirebase(): Promise<ContactMessage[]> {
  try {
    const colRef = collection(db, "leads");
    const snapshot = await getDocs(colRef);
    const leads: ContactMessage[] = [];
    snapshot.forEach((doc) => {
      leads.push(doc.data() as ContactMessage);
    });
    return leads;
  } catch (error) {
    console.warn("Error fetching leads from Firebase (using local fallback):", error);
    return [];
  }
}

export async function saveLeadToFirebase(lead: ContactMessage): Promise<void> {
  try {
    // Generate a unique ID using name, email and time to avoid overwrites
    const id = `${lead.email.replace(/[@.]/g, "_")}_${Date.now()}`;
    await setDoc(doc(db, "leads", id), {
      ...lead,
      id,
      timestamp: new Date().toISOString()
    });
    console.log("Successfully saved lead to Firestore");
  } catch (error) {
    console.warn("Error saving lead to Firestore:", error);
  }
}

export async function deleteLeadFromFirebase(id: string): Promise<void> {
  try {
    await deleteDoc(doc(db, "leads", id));
    console.log(`Successfully deleted lead ${id} from Firestore`);
  } catch (error) {
    console.warn(`Error deleting lead ${id} from Firestore:`, error);
  }
}

export async function saveLeadsBulkToFirebase(leads: any[]): Promise<void> {
  try {
    for (const lead of leads) {
      const id = lead.id || `${lead.email.replace(/[@.]/g, "_")}_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      await setDoc(doc(db, "leads", id), { ...lead, id });
    }
  } catch (error) {
    console.warn("Error saving bulk leads:", error);
  }
}

// ==========================================
// 4. APP CONFIG (DYNAMIC CONFIGURATION)
// ==========================================
const DEFAULT_CONFIG = {
  logoText: "TERALUMAX",
  logoChar: "T",
  abhishekImg: "https://ibb.co/XrjFBXHJ",
  omkarImg: "https://i.ibb.co/ccCnf21t/Whats-App-Image-2026-08-06-at-15-38-39.jpg"
};

export async function getAppConfigFromFirebase(): Promise<typeof DEFAULT_CONFIG> {
  try {
    const docRef = doc(db, "app_config", "branding");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data() as typeof DEFAULT_CONFIG;
    } else {
      // Initialize configuration in Firestore
      await setDoc(docRef, DEFAULT_CONFIG);
      return DEFAULT_CONFIG;
    }
  } catch (error) {
    console.warn("Error fetching app config from Firebase (using local fallback):", error);
    return DEFAULT_CONFIG;
  }
}

export async function saveAppConfigToFirebase(config: Partial<typeof DEFAULT_CONFIG>): Promise<void> {
  try {
    const docRef = doc(db, "app_config", "branding");
    await setDoc(docRef, config, { merge: true });
    console.log("Successfully saved app config to Firestore");
  } catch (error) {
    console.warn("Error saving app config to Firestore:", error);
  }
}

// ==========================================
// 5. SECURITY & AUTH STATUS
// ==========================================
export async function getSecurityConfigFromFirebase(): Promise<any> {
  try {
    const docRef = doc(db, "security", "admin_auth");
    const docSnap = await getDoc(docRef);
    if (docSnap.exists()) {
      return docSnap.data();
    }
    return null;
  } catch (error) {
    console.warn("Error fetching security config:", error);
    return null;
  }
}

export async function saveSecurityConfigToFirebase(config: any): Promise<void> {
  try {
    const docRef = doc(db, "security", "admin_auth");
    await setDoc(docRef, config, { merge: true });
    console.log("Successfully saved security config to Firestore");
  } catch (error) {
    console.warn("Error saving security config to Firestore:", error);
  }
}

// ==========================================
// 6. LOGS AND TELEMETRY
// ==========================================
export async function getLogsFromFirebase(type: "auth" | "vitals" | "mysql" | "analytics"): Promise<any[]> {
  try {
    const colRef = collection(db, `${type}_logs`);
    const snapshot = await getDocs(colRef);
    const logs: any[] = [];
    snapshot.forEach((doc) => {
      logs.push(doc.data());
    });
    // Order by timestamp desc if present
    return logs.sort((a, b) => new Date(b.timestamp || 0).getTime() - new Date(a.timestamp || 0).getTime());
  } catch (error) {
    console.warn(`Error fetching ${type} logs:`, error);
    return [];
  }
}

export async function addLogToFirebase(type: "auth" | "vitals" | "mysql" | "analytics", log: any): Promise<void> {
  try {
    const id = log.id || `log_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
    await setDoc(doc(db, `${type}_logs`, id), { ...log, id });
  } catch (error) {
    console.warn(`Error adding ${type} log:`, error);
  }
}

export async function saveLogsBulkToFirebase(type: "auth" | "vitals" | "mysql" | "analytics", logs: any[]): Promise<void> {
  try {
    for (const log of logs) {
      const id = log.id || `log_${Date.now()}_${Math.floor(Math.random() * 1000)}`;
      await setDoc(doc(db, `${type}_logs`, id), { ...log, id });
    }
  } catch (error) {
    console.warn(`Error saving bulk ${type} logs:`, error);
  }
}

export async function clearLogsFromFirebase(type: "auth" | "vitals" | "mysql" | "analytics"): Promise<void> {
  try {
    const colRef = collection(db, `${type}_logs`);
    const snapshot = await getDocs(colRef);
    for (const d of snapshot.docs) {
      await deleteDoc(d.ref);
    }
    console.log(`Successfully cleared ${type} logs from Firestore`);
  } catch (error) {
    console.warn(`Error clearing ${type} logs:`, error);
  }
}

// SHA-256 Hashing helper function for secure client-side and database operations
export async function hashStringSHA256(str: string): Promise<string> {
  try {
    const encoder = new TextEncoder();
    const data = encoder.encode(str);
    const hashBuffer = await crypto.subtle.digest("SHA-256", data);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    return hashArray.map(b => b.toString(16).padStart(2, "0")).join("");
  } catch (e) {
    // Standard robust fallback hash if SubtleCrypto is not supported
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = (hash << 5) - hash + char;
      hash = hash & hash;
    }
    const hashStr = Math.abs(hash).toString(16);
    return "fallback_" + hashStr.padStart(16, "0");
  }
}

